import { useRef, useMemo } from 'react'
import { Text, Float, RoundedBox } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import useAppStore from '../stores/appStore'
import { getPoem, getChapter } from '../data/poems'

function PoemDisplay() {
  const groupRef = useRef()
  const { currentChapter, currentPoem, audioPlaying, audioData } = useAppStore()

  // Get current poem data (convert 0-based index to 1-based chapter ID)
  const poemData = useMemo(() => {
    if (currentChapter === null) return null
    return getPoem(currentChapter + 1, currentPoem)
  }, [currentChapter, currentPoem])

  // Get chapter info
  const chapterData = useMemo(() => {
    if (currentChapter === null) return null
    return getChapter(currentChapter + 1)
  }, [currentChapter])

  // Organize lines by stanza with spacing
  const stanzas = useMemo(() => {
    if (!poemData || !poemData.stanzas) return []
    return poemData.stanzas
  }, [poemData])

  // Animate based on audio
  useFrame((state, delta) => {
    if (groupRef.current && audioPlaying && audioData.length > 0) {
      const avgFreq = audioData.reduce((a, b) => a + b, 0) / audioData.length
      groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.5 * (1 + avgFreq)
    }
  })

  if (!poemData) return null

  const zPosition = 6 // Position text forward in front of geometry
  const stanzaGap = 0.8 // Extra gap between stanzas
  const lineHeight = 0.5 // Height between lines

  return (
    <group ref={groupRef}>
      <Float speed={2} rotationIntensity={0.2} floatIntensity={0.5}>
        {/* Frosted Glass Background Panel */}
        <RoundedBox
          args={[14, 9, 0.1]}
          radius={0.2}
          smoothness={4}
          position={[0, 0, zPosition - 0.2]}
        >
          <meshStandardMaterial
            color="#0a0a2a"
            transparent
            opacity={0.2}
            roughness={0.2}
            metalness={0.1}
            envMapIntensity={0.5}
          />
        </RoundedBox>

        {/* Poem Title */}
        <Text
          position={[0, 4, zPosition]}
          fontSize={0.4}
          color="#D4AF37"
          anchorX="center"
          anchorY="middle"
        >
          {poemData.title}
        </Text>

        {/* Poem Stanzas - 3 stanzas × 3 lines with visual breaks */}
        {stanzas.map((stanza, stanzaIndex) => (
          <group key={stanzaIndex}>
            {stanza.map((line, lineIndex) => {
              const lineNumber = stanzaIndex * 3 + lineIndex
              const yOffset = stanzaIndex * stanzaGap
              return (
                <Text
                  key={lineNumber}
                  position={[0, 2.5 - lineNumber * lineHeight - yOffset, zPosition]}
                  fontSize={0.25}
                  color="#ffffff"
                  anchorX="center"
                  anchorY="middle"
                  maxWidth={12}
                  lineHeight={1.5}
                  textAlign="center"
                >
                  {line}
                </Text>
              )
            })}
          </group>
        ))}

        {/* Chapter Attribution */}
        {chapterData && (
          <Text
            position={[0, -3.5, zPosition]}
            fontSize={0.2}
            color="#D4AF37"
            anchorX="center"
            anchorY="middle"
          >
            {chapterData.title}
          </Text>
        )}
      </Float>
    </group>
  )
}

export default PoemDisplay
